import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  FileText,
  Pill,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ArrowLeft,
  ClipboardCheck,
} from "lucide-react";

const PharmacyintegrationDetails = () => {
  const navigate = useNavigate();

  // ✅ State for Pharmacy Workflow
  const [workflow, setWorkflow] = useState({
    stockStatus: "",
    substitute: "",
    dispensed: false,
    pharmacistNotes: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setWorkflow((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ✅ Static prescription data
  const data = {
    prescriptionID: "RX-12345",
    status: "Pending",
    prescriptionType: "E-Prescription",
    patientIDUniqueID: "P-56789",
    patientFullName: "John Doe",
    dateofBirth: "1990-05-14",
    contactNumber: "+91 98765 43210",
    emergencyContactNumber: "+91 91234 56789",
    prescribedBy: "Dr. Smith",
    prescriptionDate: "2025-09-23",
    expiryDate: "2025-10-15",
    diagnosis: "Seasonal Allergy",
    notesForPharmacist: "Dispense generic substitutes if branded unavailable.",
    medications: [
      {
        medicationID: 1,
        medicationName: "Cetirizine",
        route: "Oral",
        allowRefills: true,
        numberOfRefills: 2,
        dosage: "10mg",
        frequency: "Once Daily",
        duration: "7",
        durationUnit: "days",
        specialInstructions: "Take after dinner",
        inStock: true,
      },
      {
        medicationID: 2,
        medicationName: "Paracetamol",
        route: "Oral",
        allowRefills: false,
        numberOfRefills: 0,
        dosage: "500mg",
        frequency: "Twice Daily",
        duration: "5",
        durationUnit: "days",
        specialInstructions: "",
        inStock: false,
      },
    ],
    testRecommendations: [
      {
        testRecommendationID: 1,
        testName: "Blood CBC",
        priority: "High",
        remarks: "Check for infection",
      },
      {
        testRecommendationID: 2,
        testName: "Allergy Panel",
        priority: "Normal",
        remarks: "Verify allergic triggers",
      },
    ],
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className=" mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-indigo-600 mb-6 group"
        >
          <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back</span>
        </button>

        {/* Header Card */}
        <div className=" text-white rounded-xl shadow-lg p-6 mb-8" style={{backgroundColor:'#111827'}}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold">Prescription Details</h1>
              <p className="text-indigo-200 mt-1">
                Prescription ID: #{data.prescriptionID}
              </p>
            </div>
            <div className="flex space-x-2 mt-4 sm:mt-0">
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                {data.status}
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {data.prescriptionType}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Patient Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center">
                <User className="w-5 h-5 mr-2 text-blue-600" /> Patient Info
              </h2>
              <p className="font-medium">{data.patientFullName}</p>
              <p className="text-sm text-gray-500">ID: {data.patientIDUniqueID}</p>
              <p className="text-sm text-gray-500">
                DOB: {formatDate(data.dateofBirth)}
              </p>
              <p className="mt-2 text-sm text-gray-600">📞 {data.contactNumber}</p>
              <p className="text-sm text-gray-600">
                Emergency: {data.emergencyContactNumber}
              </p>
            </div>

            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-green-600" /> Prescription Info
              </h2>
              <p>
                <b>Prescribed By:</b> {data.prescribedBy}
              </p>
              <p>
                <b>Issue Date:</b> {formatDate(data.prescriptionDate)}
              </p>
              <p>
                <b>Expiry Date:</b> {formatDate(data.expiryDate)}
              </p>
              <p className="mt-2">
                <b>Diagnosis:</b> {data.diagnosis}
              </p>
              <p className="mt-2 text-sm bg-yellow-50 p-2 rounded-lg border border-yellow-200">
                {data.notesForPharmacist}
              </p>
            </div>
          </div>

          {/* Medications */}
          {/* Medications */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Pill className="w-5 h-5 mr-2 text-purple-600" /> Medications
              </h2>
              {data.medications.map((med) => (
                <div
                  key={med.medicationID}
                  className="border rounded-lg p-4 mb-4 flex justify-between items-start hover:shadow-md transition"
                >
                  {/* Left: Medicine Details */}
                  <div>
                    <h3 className="font-semibold">{med.medicationName}</h3>
                    <p className="text-sm text-gray-600">{med.route}</p>
                    <p>
                      <b>Dosage:</b> {med.dosage}
                    </p>
                    <p>
                      <b>Frequency:</b> {med.frequency}
                    </p>
                    <p>
                      <b>Duration:</b> {med.duration} {med.durationUnit}
                    </p>
                    <p>
                      <b>Refills:</b>{" "}
                      {med.allowRefills ? med.numberOfRefills : "Not Allowed"}
                    </p>

                    {med.specialInstructions && (
                      <p className="text-sm bg-blue-50 p-2 mt-2 rounded border border-blue-200">
                        {med.specialInstructions}
                      </p>
                    )}
                  </div>

                  {/* Right: Stock Status & Substitute */}
                  <div className="text-right min-w-[150px]">
                    {med.inStock ? (
                      <span className="px-3 py-1 bg-green-100 text-green-700 border border-green-300 rounded-full text-sm font-medium">
                        In Stock
                      </span>
                    ) : (
                      <div className="space-y-2">
                        <span className="px-3 py-1 bg-red-100 text-red-700 border border-red-300 rounded-full text-sm font-medium">
                          Out of Stock
                        </span>

                        {/* Substitute Suggestion */}
                        <div className="mt-3 bg-gray-50 p-3 rounded-lg border border-gray-200 shadow-sm">
                          <label className="flex items-center text-xs font-medium text-gray-600 mb-2">
                            💊 Suggest Substitute
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              name={`substitute-${med.medicationID}`}
                              value={workflow.substitute}
                              onChange={handleChange}
                              placeholder="Enter alternative medicine"
                              className="w-full p-2 pl-3 pr-10 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                            />
                            <span className="absolute right-3 top-2.5 text-gray-400 text-sm">✏️</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* ✅ Pharmacy Workflow */}
        <div className="mt-10 bg-white rounded-xl shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <ClipboardCheck className="w-6 h-6 text-indigo-600 mr-2" />
            Pharmacy Workflow
          </h2>

          {/* Step: Dispensing */}
          <div className="border-l-4 border-green-500 pl-4">
            <h3 className="font-semibold text-gray-900 flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              Dispensing & Tracking
            </h3>
            <div className="flex items-center mt-3">
              <input
                type="checkbox"
                name="dispensed"
                checked={workflow.dispensed}
                onChange={handleChange}
                className="mr-2"
              />
              <label className="text-sm text-gray-700">Mark as Dispensed</label>
            </div>

            <label className="block text-sm mt-4 font-medium text-gray-700">
              Pharmacist Notes
            </label>
            <textarea
              name="pharmacistNotes"
              value={workflow.pharmacistNotes}
              onChange={handleChange}
              placeholder="Enter any pharmacist notes..."
              className="mt-1 p-2 border rounded-lg w-full focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end mt-6">
            <button
              onClick={() => alert(JSON.stringify(workflow, null, 2))}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow transition"
            >
              Save Workflow
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PharmacyintegrationDetails;
